import { useCallback, useContext, useEffect, useState } from "react"
import { HashLink } from "react-router-hash-link"
import { motion } from "framer-motion"
import { ReiterType } from "../types"
import { P } from "../../auth/permissions"
import { Authorized } from "../authentication/Authorized"
import { useAbility } from "../contexts/AbilityContext"
import { ReactComponent as LOGOUT } from "../../assets/LogoutICON.svg"
import { ReactComponent as ARROWLEFT } from "../../assets/ChevronLeft.svg"
import { ReactComponent as BACKARROWLEFT } from "../../assets/BackArrowLeft.svg"
import { ReactComponent as ARROWRIGHT } from "../../assets/ChevronRight.svg"
import { ReactComponent as HELP } from "../../assets/Help.svg"
import { ReactComponent as HOUSE } from "../../assets/HouseICON.svg"
import { ReactComponent as BREAFCASE } from "../../assets/Breafcase.svg"
import { ReactComponent as TASKS } from "../../assets/Task.svg"
import { ReactComponent as PEOPLE } from "../../assets/People.svg"
import { ReactComponent as CASH } from "../../assets/Cash.svg"
import { ReactComponent as DASHBOARD } from "../../assets/Dashboard.svg"
import { ReactComponent as STATISTICS } from "../../assets/Statistics.svg"
import { ReactComponent as CALENDAR } from "../../assets/CalendarICON.svg"
import { ReactComponent as BELL } from "../../assets/Bell.svg"
import { ReactComponent as PIN } from "../../assets/MapPinICON.svg"
import { ReactComponent as GLOBUS } from "../../assets/Globus.svg"
import { ReactComponent as SETTINGS } from "../../assets/Settings.svg"
import { ReactComponent as PHONE } from "../../assets/Phone.svg"
import { ReactComponent as TRUCK } from "../../assets/Truck.svg"
import { ReactComponent as DOCUMENT } from "../../assets/DocumentDuplicate.svg"
import { ReactComponent as REPORT } from "../../assets/Document.svg"
import { useLocation, useNavigate } from "react-router-dom"
import "../../style/pseudo.css"
import "../../style/hidescroll.css"
import { SelfUserContext } from "../contexts/SelfUserContext"
import { ModalContext } from "../contexts/ModalContext"
import { SidePanelContext } from "../contexts/SidePanelContext"
import { clearSessionStorageByPrefix } from "../../services/functions"
import { Navigation } from "../../api/constants"
import { KeyRound, Route } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"

type HeaderProps = {
    logo?: string
}

export default function Header({ logo }: HeaderProps) {
    const auth = useAuth()
    const ability = useAbility()
    const userSelf = useContext(SelfUserContext)

    const [mini, setmini] = useState(false)

    const [url, seturl] = useState("")

    const location = useLocation()
    useEffect(() => {
        seturl(window.location.href)
    }, [location])

    // tracking page
    const isPageCurrent = useCallback((page: string) => location.pathname.includes(page), [location])

    // track sizing
    const [windowSize, setwindowSize] = useState(window.innerWidth)
    useEffect(() => {
        function handleResize() {
            setwindowSize(window.innerWidth)
            window.innerWidth <= 1024 && setmini(true)
        }

        window.addEventListener("resize", handleResize)
        handleResize()
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    // Navigation
    const navigate = useNavigate()
    const modalContext = useContext(ModalContext)
    const sidePanelContext = useContext(SidePanelContext)
    const handleNavigateBack = () => {
        navigate(-1)
        modalContext?.onScreenChangeCloseAllModals()
        sidePanelContext?.onScreenChangeCloseAllSidePanels()
    }

    return (
        <>
            <motion.div
                initial={{
                    x: windowSize > 1024 ? 0 : "-16rem",
                    width: windowSize < 640 ? "0rem" : windowSize > 1024 ? "1rem" : "16rem",
                }}
                animate={{
                    x: !mini && windowSize <= 1024 ? 0 : 0,
                    width: windowSize < 640 && mini ? "0rem" : mini && windowSize <= 1024 ? "1rem" : !mini && windowSize <= 1024 ? "16rem" : mini && windowSize > 1024 ? "4rem" : "16rem",
                }}
                transition={{
                    duration: 0.5,
                    type: "ease",
                }}
                className="h-screen bg-primary flex flex-col items-center pt-16 z-20 absolute lg:relative">
                <motion.button
                    initial={{
                        bottom: windowSize >= 640 ? "8rem" : undefined,
                        top: windowSize < 640 ? "2rem" : undefined,
                    }}
                    animate={{
                        bottom: windowSize >= 640 ? (mini ? "8rem" : "5rem") : undefined,
                        // top: windowSize < 640 && mini ? "8rem" : "5rem",
                    }}
                    transition={{
                        duration: 0.5,
                        type: "ease",
                    }}
                    className={`text-base-100 absolute z-20 border ${windowSize >= 1024 ? "p-3" : "p-2"} rounded-default bg-primary ${windowSize >= 640 ? "-right-5" : "-right-6"} `}
                    onClick={() => setmini(!mini)}>
                    {!mini ? <ARROWLEFT className={windowSize >= 1024 ? "w-6 h-6" : "w-4 h-4"} /> : <ARROWRIGHT className={windowSize >= 1024 ? "w-6 h-6" : "w-4 h-4"} />}
                </motion.button>
                <div
                    onClick={handleNavigateBack}
                    className={`${!mini && " gap-1 left-4  border-b border-base-100"} flex text-base-100 absolute top-4  text-xs items-center justify-center cursor-pointer`}>
                    <BACKARROWLEFT className={`${mini ? "w-5 h-5" : "w-3.5 h-3.5"} font-semibold`} />
                    {!mini && <p className="pb-[0.07rem]">zurück</p>}
                </div>
                {!mini && logo && (
                    <div className="w-[80%] mx-auto h-20 p-4 bg-white rounded-default">
                        <img alt="Hausmanager-App Logo" className=" object-cover max-h-full mx-auto" src={logo} />
                    </div>
                )}
                {/* Main Content */}
                <div className={`flex flex-col w-full grow  pt-4 items-center overflow-y-scroll overflow-x-hidden`}>
                    <Authorized allOf={[P.termine_execute]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/kalendar/" + userSelf?.user._id)
                            }}
                            text="Kalender"
                            link={"/kalender/" + userSelf?.user?._id}
                            open={isPageCurrent("/kalender") || isPageCurrent("/termin")}
                            icon={<CALENDAR className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized anyOf={[P.termine_execute, P.termine_management]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl(Navigation.TOURS)
                            }}
                            text="Touren"
                            link={Navigation.TOURS}
                            open={isPageCurrent("/touren")}
                            icon={<Route className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.dashboard_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/dashboard")
                            }}
                            text="Dashboard"
                            link="/dashboard"
                            responsive={windowSize <= 1024}
                            open={isPageCurrent("/dashboard")}
                            icon={<STATISTICS className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized anyOf={[P.buchhaltung_read, P.buchhaltung_read_restricted]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/buchhaltung")
                            }}
                            text="Buchhaltung"
                            link={ability.can(P.buchhaltung_zahlungen_read.action, P.buchhaltung_zahlungen_read.subject) ? "/buchhaltung/zahlungen" : "/buchhaltung/rechnungen"}
                            responsive={windowSize <= 1024}
                            open={isPageCurrent("/buchhaltung")}
                            icon={<CASH className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.aufgaben_read_write, P.aufgaben_create, P.aufgaben_delete, P.aufgaben_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/aufgaben")
                            }}
                            text="Aufgaben"
                            link="/aufgaben"
                            open={isPageCurrent("/aufgaben")}
                            icon={<TASKS className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.aufgaben_read_write, P.aufgaben_create, P.aufgaben_delete, P.aufgaben_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/berichte")
                            }}
                            text="Berichte"
                            link="/berichte"
                            open={isPageCurrent("/berichte")}
                            icon={<REPORT className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.anfragen]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/anfragen")
                            }}
                            text="Anfragen"
                            link="/anfragen"
                            open={isPageCurrent("/anfragen")}
                            icon={<BELL className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.mitarbeiter_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/mitarbeiter")
                            }}
                            text="Mitarbeiter"
                            link="/mitarbeiter"
                            open={isPageCurrent("/mitarbeiter")}
                            icon={<PEOPLE className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.mitarbeiter_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/overview")
                            }}
                            text="Übersicht"
                            link="/overview"
                            open={isPageCurrent("/overview")}
                            icon={<DASHBOARD className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.abwesenheiten_read]}>
                        <NavReiter
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            responsive={windowSize <= 1024}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/abwesenheiten")
                            }}
                            text="Abwesenheiten"
                            link="/abwesenheiten"
                            open={isPageCurrent("/abwesenheiten")}
                            icon={<GLOBUS className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/objekte")
                            }}
                            text="Objekte"
                            link="/objekte"
                            open={isPageCurrent("/objekte")}
                            icon={<HOUSE className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl(Navigation.KEYS)
                            }}
                            text="Schlüssel"
                            link={Navigation.KEYS}
                            open={isPageCurrent(Navigation.KEYS)}
                            icon={<KeyRound className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.kunden_read]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/kunden")
                            }}
                            text="Kunden"
                            link="/kunden"
                            open={isPageCurrent("/kunden")}
                            icon={<BREAFCASE className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/karte")
                            }}
                            text="Karte"
                            link="/karte"
                            open={isPageCurrent("/karte")}
                            icon={<PIN className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.global_settings_read_write]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/einstellungen")
                            }}
                            text="Einstellungen"
                            link="/einstellungen"
                            open={isPageCurrent("/einstellungen")}
                            icon={<SETTINGS className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            disabled
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/telefonassistent")
                            }}
                            text="Telefonassistent"
                            link="/telefonassistent"
                            open={isPageCurrent("/telefonassistent")}
                            icon={<PHONE className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            disabled
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/fuhrpark")
                            }}
                            text="Fuhrpark"
                            link="/fuhrpark"
                            open={isPageCurrent("/fuhrpark")}
                            icon={<TRUCK className="w-6 h-6" />}
                        />
                    </Authorized>
                    <Authorized allOf={[P.objekte_read]}>
                        <NavReiter
                            disabled
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            url={url}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                                seturl("/dateimanagement")
                            }}
                            text="Dateimanagement"
                            link="/dateimanagement"
                            open={isPageCurrent("/dateimanagement")}
                            icon={<DOCUMENT className="w-6 h-6" />}
                        />
                    </Authorized>
                </div>
                {/* Bottom Row */}
                <div className="flex flex-col w-full h-fit pb-4 border-t">
                    <Authorized allOf={[P.help_read_write]}>
                        <NavReiter
                            responsive={windowSize <= 1024}
                            trigger={() => windowSize <= 1024 && setmini(!mini)}
                            mini={mini}
                            func={() => {
                                clearSessionStorageByPrefix()
                                modalContext?.onScreenChangeCloseAllModals()
                                sidePanelContext?.onScreenChangeCloseAllSidePanels()
                            }}
                            text="Hilfe"
                            link="/hilfe"
                            open={isPageCurrent("/hilfe")}
                            icon={<HELP className="w-6 h-6" />}
                        />
                    </Authorized>
                    <NavReiter
                        responsive={windowSize <= 1024}
                        trigger={() => windowSize <= 1024 && setmini(!mini)}
                        mini={mini}
                        func={() => void auth.logout()}
                        text="Logout"
                        extendText={auth.session?.user.email}
                        truncate
                        link="/login"
                        open={isPageCurrent("/logout")}
                        icon={<LOGOUT className="w-6 h-6" />}
                    />
                </div>
            </motion.div>
        </>
    )
}

function NavReiter(props: ReiterType) {
    return (
        <HashLink to={props.link} className="w-full">
            <motion.div
                initial={{
                    paddingLeft: "2rem",
                    paddingRight: "2rem",
                }}
                animate={{
                    paddingLeft: props.mini ? "1rem" : "2rem",
                    paddingRight: props.mini ? "1rem" : "2rem",
                }}
                onClick={() => {
                    props.func()
                    props.trigger(true)
                }}
                className={`w-full text-base 2xl:text-lg ${props.disabled ? "text-gray-300 opacity-50" : "text-base-100"} flex h-12 2xl:h-14 cursor-pointer items-center  ${
                    props.mini ? "justify-center" : "justify-start"
                }
                ${!props.responsive && (props.open || props?.url?.endsWith(props.link) ? "bg-base-100 text-primary ml-2 rounded-l-default relative -z-10" : " hover:bg-black/10")}
                `}>
                {!props.responsive && (props.open || props?.url?.endsWith(props.link)) && (
                    <>
                        <div className={`bg-none w-fit h-fit absolute right-[0.48rem] ${props.mini ? "-top-[0.99rem]" : "-top-[0.98rem]"}`}>
                            <div className="upper_corner bg-primary h-full w-full" />
                        </div>
                        <div className="bg-none w-fit h-fit absolute right-[0.48rem] -bottom-[0.98rem]">
                            <div className="bottom_corner bg-primary h-full w-full" />
                        </div>
                    </>
                )}
                {props.icon}
                {!props.mini && (
                    <p className={`pl-4 font-semibold leading-4 text-base ${props.truncate && "truncate"}`}>
                        {props.text}{" "}
                        {props.extendText && (
                            <span className="text-xs font-normal 2xl:text-sm">
                                <br />
                                {props.extendText}
                            </span>
                        )}
                    </p>
                )}
            </motion.div>
        </HashLink>
    )
}
